import { Box, Flex, Text } from "@nulogy/components";
import CollapsibleSection from "components/common/CollapsibleSection";
import FieldRow from "components/common/FieldRow";
import HalfField from "components/common/HalfField";
import Section from "components/common/Section";
import DatePicker from "components/controls/DatePicker";
import Form from "components/controls/Form";
import Input from "components/controls/Input";
import LineSelect from "components/controls/LineSelect";
import LockToggle from "components/controls/LockToggle";
import Textarea from "components/controls/Textarea";
import WorkOrderSelect from "components/controls/WorkOrderSelect";
import {
  formatDateTime,
  formatDuration,
  formatEfficiency,
  formatLabor,
  formatPerformance,
  formatProductionRate,
} from "components/helpers/format";
import ProductionDetail from "components/pages/schedule/workBlocks/ProductionDetail";
import ProductionDetailsSummary from "components/pages/schedule/workBlocks/ProductionDetailsSummary";
import WorkBlock from "domain/WorkBlock";
import React from "react";
import styled from "styled-components";
import DateTime from "utils/DateTime";
import Num from "utils/Num";

const inputWidth = 96;
const gapWidth = 16; // x2

const Divider = styled(Box)(({ theme }) => ({
  borderTop: `1px solid ${theme.colors.grey}`,
}));

export default function CreateWorkBlockForm(props) {
  const {
    onClose,
    onSave,
    lines,
    workOrders,
    setErrors,
    blockData: { block, startDate, endDate },
    applicationContext,
    workBlockModalUIState,
  } = props;
  const { settings, customerSettings } = applicationContext;
  const { workBlockLayout } = customerSettings;
  const initialWorkOrder = workOrders[0];
  const { line: selectedLine } = block;

  const initialValues = {
    ...WorkBlock.buildWithWorkTime({
      efficiencyOverride: "",
      laborOverride: "",
      line: selectedLine,
      lockedOnSchedule: false,
      performanceOverride: "",
      productionRateOverride: "",
      workOrder: initialWorkOrder,
      workTimeEndsAt: DateTime.toISO8601(endDate),
      workTimeStartsAt: DateTime.toISO8601(startDate),
    }),
    teardownTimeDurationHours: 0,
  };

  let { overlappingPlannedBreaks } = initialValues;

  const { productionDetailsOpen, toggleProductionDetails } = workBlockModalUIState;

  function findSelectedWorkOrder(id) {
    return workOrders.find((workOrder) => workOrder.id === id);
  }

  function findSelectedLine(id) {
    return lines.find((line) => line.id === id) || selectedLine;
  }

  const validate = (data) => {
    const errors = WorkBlock.validateInput(data);

    setErrors(errors);

    return errors;
  };

  const handleSave = (data) =>
    new Promise(() => {
      onSave(
        WorkBlock.updateBlock(initialValues, {
          ...data,
          __typename: "WorkBlock",
          efficiencyOverride: Num.percentToDecimal(data.efficiencyOverride) || null,
          laborOverride: data.laborOverride || null,
          performanceOverride: Num.percentToDecimal(data.performanceOverride) || null,
          productionRateOverride: data.productionRateOverride || null,
        }),
      );
      onClose();
    });

  const onTeardownTimeChange = ({ value, setFieldValue }) => {
    setFieldValue("teardownTimeDuration", DateTime.hoursToSeconds(value));
  };

  const onWorkOrderChange = ({ value: id, setFieldValue }) => {
    const workOrder = findSelectedWorkOrder(id);
    setFieldValue("workOrder[notes]", workOrder.notes);
  };

  const recalculateOverlappingPlannedBreaks = (workBlock, line) => {
    if (workBlock) {
      const newLine = line || findSelectedLine(workBlock.line.id);
      overlappingPlannedBreaks = WorkBlock.overlappingPlannedBreaks({ ...workBlock, line: newLine });
    }
  };

  const calculateEffectiveProductionRate = (values) => {
    const workOrder = findSelectedWorkOrder(values.workOrder.id);
    const line = findSelectedLine(values.line.id);

    return WorkBlock.effectiveProductionRate(
      {
        ...workOrder,
        ...values,
        efficiencyOverride: Num.percentToDecimal(values.efficiencyOverride),
        line,
        performanceOverride: Num.percentToDecimal(values.performanceOverride),
      },
      settings,
    );
  };

  const calculateExpectedQuantity = (values) => {
    const workOrder = findSelectedWorkOrder(values.workOrder.id);
    const line = findSelectedLine(values.line.id);
    recalculateOverlappingPlannedBreaks(values, line);

    return WorkBlock.quantity({
      ...workOrder,
      ...values,
      efficiencyOverride: Num.percentToDecimal(values.efficiencyOverride),
      line,
      overlappingPlannedBreaks,
      performanceOverride: Num.percentToDecimal(values.performanceOverride),
    });
  };

  const calculateLineEfficiency = (values) => {
    const line = findSelectedLine(values.line.id);

    return WorkBlock.lineEfficiency({ line });
  };

  const getUnitOfMeasure = (values) => {
    const workOrder = findSelectedWorkOrder(values.workOrder.id);

    return workOrder.unitOfMeasure;
  };

  const getProductionRateDependsOnNumberOfPeople = (values) => {
    const workOrder = findSelectedWorkOrder(values.workOrder.id);

    return workOrder.productionRateDependsOnNumberOfPeople;
  };

  const getStandardProductionRate = (values) => {
    const workOrder = findSelectedWorkOrder(values.workOrder.id);

    return workOrder.standardProductionRate;
  };

  const getPerformance = (values) => {
    const workOrder = findSelectedWorkOrder(values.workOrder.id);

    return workOrder.performance;
  };

  const getStandardPeople = (values) => {
    const workOrder = findSelectedWorkOrder(values.workOrder.id);

    return workOrder.standardPeople;
  };

  const formattedStandardPeople = (values) => formatLabor(getStandardPeople(values));
  const formattedLineEfficiency = (values) => formatEfficiency(calculateLineEfficiency(values), { suffix: false });

  if (!workOrders.length) {
    return (
      <Flex alignItems="center" backgroundColor="whiteGrey" justifyContent="center" minHeight="300px" padding="10px">
        <Text fontSize="large" fontWeight="bold">
          No work orders in search results to schedule
        </Text>
      </Flex>
    );
  }

  const handleFormChange = (e) => {
    recalculateOverlappingPlannedBreaks(e.values);
  };

  return (
    <Form
      id="createWorkBlock"
      initialValues={initialValues}
      onChange={handleFormChange}
      onSave={handleSave}
      validate={validate}
    >
      {({ values, values: { lockedOnSchedule } }) => (
        <>
          <Section controls={<LockToggle field="lockedOnSchedule" />} title="Scheduling details">
            <Flex flexDirection="column" gap="x2">
              <FieldRow marginBottom="0px">
                <WorkOrderSelect
                  disabled={lockedOnSchedule}
                  onChange={onWorkOrderChange}
                  workBlockLayout={workBlockLayout}
                  workOrders={workOrders}
                />
              </FieldRow>
              <FieldRow marginBottom="0px">
                <LineSelect disabled={lockedOnSchedule} lines={lines} />
              </FieldRow>
              <Flex className="customDatePicker" columnGap="x2" rowGap="x2" width="100%">
                <HalfField>
                  <DatePicker
                    className="spec-work-time-starts-at"
                    disabled={lockedOnSchedule}
                    field="workTimeStartsAt"
                    labelText="Work starts"
                  />
                </HalfField>
                <HalfField>
                  <DatePicker
                    className="spec-work-time-ends-at"
                    disabled={lockedOnSchedule}
                    field="workTimeEndsAt"
                    labelText="Work ends"
                  />
                </HalfField>
              </Flex>
              {overlappingPlannedBreaks.length > 0 && (
                <HalfField labelText="Planned breaks" mb="0px">
                  {overlappingPlannedBreaks.map(({ plannedBreakName, duration }) => (
                    <Text key={plannedBreakName}>{`${plannedBreakName}: ${formatDuration(duration)}`}</Text>
                  ))}
                </HalfField>
              )}
            </Flex>
          </Section>
          <Section className="spec-teardown-time-section" data-testid="teardown-time-section" title="Teardown time">
            <Flex columnGap="x2" flexWrap="wrap" rowGap="x2">
              <HalfField mb="0px">
                <Input
                  className="spec-teardown-time-label"
                  data-testid="teardown-time-label"
                  disabled={lockedOnSchedule}
                  field="teardownTimeLabel"
                  labelText="Label"
                />
              </HalfField>
              <HalfField mb="0px">
                <Input
                  data-testid="teardown-time-duration"
                  disabled={lockedOnSchedule}
                  field="teardownTimeDurationHours"
                  inputWidth={`${inputWidth}px`}
                  labelText="Duration (h)"
                  onChange={onTeardownTimeChange}
                  step="any"
                  suffix="hours"
                  type="number"
                />
              </HalfField>
              {values.teardownTimeDurationHours > 0 && (
                <>
                  <HalfField labelText="Teardown starts" mb="0px">
                    {formatDateTime(values.workTimeEndsAt)}
                  </HalfField>
                  <HalfField labelText="Teardown ends" mb="0px">
                    {formatDateTime(
                      DateTime.addDuration(values.workTimeEndsAt, values.teardownTimeDuration, "seconds"),
                    )}
                  </HalfField>
                </>
              )}
            </Flex>
          </Section>
          <CollapsibleSection
            isOpen={productionDetailsOpen}
            onToggle={toggleProductionDetails}
            title="Production details"
          >
            <ProductionDetail
              disabled={lockedOnSchedule}
              field="productionRateOverride"
              label="Standard production rate"
              overrideValue={values.productionRateOverride}
              specClassName="spec-base-production-rate"
              value={formatProductionRate({
                productionRate: getStandardProductionRate(values),
                unitOfMeasure: getUnitOfMeasure(values),
              })}
            />
            <ProductionDetail
              disabled={lockedOnSchedule}
              field="performanceOverride"
              label="Performance"
              overrideValue={values.performanceOverride}
              specClassName="spec-performance"
              value={formatPerformance(getPerformance(values))}
            />
            <ProductionDetail
              additionalInfo={
                !getProductionRateDependsOnNumberOfPeople(values) && (
                  <span>
                    Overriding labor will <strong>not</strong> affect production rate
                  </span>
                )
              }
              data-testid="standard-people-detail"
              disabled={lockedOnSchedule}
              field="laborOverride"
              label="Standard people"
              overrideValue={values.laborOverride}
              specClassName="spec-standard-people"
              value={formattedStandardPeople(values)}
            />
            <ProductionDetail
              disabled={lockedOnSchedule}
              field="efficiencyOverride"
              label="Line efficiency"
              overrideValue={values.efficiencyOverride}
              specClassName="spec-line-efficiency"
              value={formattedLineEfficiency(values)}
            />
            <Divider width={`calc(50% + ${gapWidth / 2}px + ${inputWidth}px)`} />
            <ProductionDetailsSummary
              effectiveProductionRate={calculateEffectiveProductionRate(values)}
              expectedQuantity={calculateExpectedQuantity(values)}
              unitOfMeasure={getUnitOfMeasure(values)}
            />
          </CollapsibleSection>
          <Textarea className="spec-work-order-notes" field="workOrder[notes]" placeholder="Add a work order note..." />
        </>
      )}
    </Form>
  );
}
